import React from 'react';
import { Button, Space, Tag, Tooltip } from 'antd';
import { DownloadOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import type { DocumentRegistryRow } from '../../store/api/documentRegistryApi';
import type { ApplicationDocumentRow } from '../../utils/applicationDocumentRegistry';
import { resolveDocumentTypeLabel } from '../../utils/documentDisplayName';
import { formatDocumentUiDateTime } from '../../utils/documentUiFormat';
import DocumentStatusBadge from './DocumentStatusBadge';
import { DataTable } from '../../ui';

export type DocumentTableRow = DocumentRegistryRow | ApplicationDocumentRow;

type DocumentTableVariant = 'registry' | 'application';

type DocumentTableProps = {
  variant: DocumentTableVariant;
  dataSource: DocumentTableRow[];
  loading?: boolean;
  onPreview: (row: DocumentTableRow) => void;
  onDownload: (row: DocumentTableRow) => void;
  onDelete: (row: DocumentTableRow) => void;
};

function isApplicationRow(row: DocumentTableRow): row is ApplicationDocumentRow {
  return 'source' in row && !('sourceLabel' in row);
}

const DocumentTable: React.FC<DocumentTableProps> = ({
  variant,
  dataSource,
  loading = false,
  onPreview,
  onDownload,
  onDelete,
}) => {
  const actionsColumn = {
    title: 'Действия',
    key: 'actions',
    width: 110,
    render: (row: DocumentTableRow) => (
      <Space size="small">
        <Tooltip title="Просмотр">
          <Button type="text" size="small" icon={<EyeOutlined />} onClick={() => void onPreview(row)} />
        </Tooltip>
        <Tooltip title="Скачать">
          <Button type="text" size="small" icon={<DownloadOutlined />} onClick={() => void onDownload(row)} />
        </Tooltip>
        <Tooltip title="Удалить">
          <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => onDelete(row)} />
        </Tooltip>
      </Space>
    ),
  };

  const registryColumns = [
    {
      title: 'Дата',
      key: 'createdAt',
      width: 130,
      render: (row: DocumentRegistryRow) => formatDocumentUiDateTime(row.createdAt),
    },
    {
      title: 'Тип документа',
      key: 'typeLabel',
      width: 170,
      render: (row: DocumentRegistryRow) => (
        <span>
          {resolveDocumentTypeLabel({
            documentType: row.documentType,
            customDocumentName: row.customDocumentName,
          }) || row.typeLabel}
        </span>
      ),
    },
    {
      title: 'Файл',
      key: 'fileName',
      render: (row: DocumentRegistryRow) => (
        <Tooltip title={row.fileName}>
          <span
            style={{
              maxWidth: 220,
              display: 'inline-block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {row.fileName}
          </span>
        </Tooltip>
      ),
    },
    {
      title: 'Заявка',
      key: 'application',
      width: 110,
      render: (row: DocumentRegistryRow) =>
        row.application?.internalNumber ? `№${row.application.internalNumber}` : '—',
    },
    {
      title: 'Клиент',
      key: 'client',
      width: 140,
      render: (row: DocumentRegistryRow) => row.application?.clientName || '—',
    },
    {
      title: 'Маршрут',
      key: 'route',
      width: 180,
      render: (row: DocumentRegistryRow) => (
        <Tooltip title={row.route?.label || undefined}>
          <span
            style={{
              maxWidth: 170,
              display: 'inline-block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {row.route?.label || '—'}
          </span>
        </Tooltip>
      ),
    },
    {
      title: 'Перевозчик',
      key: 'carrier',
      width: 140,
      render: (row: DocumentRegistryRow) => row.route?.carrierName || '—',
    },
    {
      title: 'Статус',
      key: 'lifecycleState',
      width: 120,
      render: (row: DocumentRegistryRow) => (
        <DocumentStatusBadge
          status={row.lifecycleState ?? row.documentEngine?.lifecycleState}
        />
      ),
    },
    {
      title: 'Источник',
      key: 'sourceLabel',
      width: 110,
      render: (row: DocumentRegistryRow) => <Tag>{row.sourceLabel}</Tag>,
    },
    actionsColumn,
  ];

  const applicationColumns = [
    {
      title: 'Дата',
      key: 'createdAt',
      width: 130,
      render: (row: ApplicationDocumentRow) => formatDocumentUiDateTime(row.createdAt),
    },
    {
      title: 'Название',
      key: 'fileName',
      render: (row: ApplicationDocumentRow) => (
        <Tooltip title={row.fileName}>
          <span
            style={{
              maxWidth: 220,
              display: 'inline-block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {row.fileName}
          </span>
        </Tooltip>
      ),
    },
    {
      title: 'Тип документа',
      key: 'typeLabel',
      width: 170,
      render: (row: ApplicationDocumentRow) => row.typeLabel,
    },
    {
      title: 'Источник',
      key: 'source',
      width: 110,
      render: (row: ApplicationDocumentRow) => <Tag>{row.source}</Tag>,
    },
    {
      title: 'Маршрут',
      key: 'routeLabel',
      width: 180,
      render: (row: ApplicationDocumentRow) => row.routeLabel || '—',
    },
    {
      title: 'Стоп',
      key: 'stopLabel',
      width: 160,
      render: (row: ApplicationDocumentRow) => row.stopLabel || '—',
    },
    {
      title: 'Комментарий',
      key: 'comment',
      render: (row: ApplicationDocumentRow) => row.comment || '—',
    },
    actionsColumn,
  ];

  const columns = variant === 'application' ? applicationColumns : registryColumns;

  return (
    <DataTable
      columns={columns as any}
      dataSource={dataSource}
      rowKey="id"
      loading={loading}
      pagination={false}
      size="small"
      scroll={{ x: 'max-content' }}
    />
  );
};

export default DocumentTable;
export { isApplicationRow };
